import { Calendar, Copy, Eye, Pencil, RotateCcw, Tag, Trash2 } from 'lucide-react'

import { OFFER_TYPE_GUIDE } from '@iws/theme-schema/campaign'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'

import { OfferModel } from '@/types'

import { CampaignStatusBadge } from './CampaignStatusBadge'

/** The hero block's image, else the legacy hero image. */
const heroImageOf = (c: OfferModel.Offer) =>
  (c.ui_content?.blocks?.find((b) => b.type === 'hero')?.props.image as string | undefined) || c.ui_content?.hero_image

interface Props {
  data: OfferModel.Offer[]
  loading: boolean
  onPreview: (campaign: OfferModel.Offer) => void
  previewLoading: boolean
  onEdit: (campaign: OfferModel.Offer) => void
  onToggle: (campaign: OfferModel.Offer) => void
  onDuplicate: (campaign: OfferModel.Offer) => void
  onDelete: (campaign: OfferModel.Offer) => void
  onRestore: (campaign: OfferModel.Offer) => void
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

export const CampaignCardGrid = ({
  data,
  loading,
  onPreview,
  previewLoading,
  onEdit,
  onToggle,
  onDuplicate,
  onDelete,
  onRestore,
}: Props) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-56 rounded-lg" />
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {data.map((campaign) => (
        <Card key={campaign._id} className="overflow-hidden">
          {/* Hero image */}
          <div className="aspect-video overflow-hidden border-b border-border-subtle bg-subdued">
            {heroImageOf(campaign) ? (
              <img src={heroImageOf(campaign)} alt={campaign.name} className="size-full object-cover" />
            ) : (
              <div className="flex size-full items-center justify-center text-sm text-muted-foreground">No preview</div>
            )}
          </div>

          <div className="space-y-3 p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 space-y-1.5">
                <p className="truncate text-sm font-semibold text-ink">{campaign.name}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <CampaignStatusBadge campaign={campaign} />
                  <Badge tone="outline">
                    <Tag className="size-3" />
                    {OFFER_TYPE_GUIDE[campaign.type]?.label ?? campaign.type}
                  </Badge>
                  {campaign.ui_content?.badge_text && <Badge tone="soft">{campaign.ui_content.badge_text}</Badge>}
                </div>
              </div>

              <div className="flex shrink-0 items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  title="Preview"
                  aria-label="Preview campaign"
                  disabled={previewLoading}
                  onClick={() => onPreview(campaign)}
                >
                  <Eye className="text-muted-foreground" />
                </Button>
                <Button variant="ghost" size="icon" title="Edit" aria-label="Edit campaign" onClick={() => onEdit(campaign)}>
                  <Pencil className="text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Duplicate"
                  aria-label="Duplicate campaign"
                  onClick={() => onDuplicate(campaign)}
                >
                  <Copy className="text-muted-foreground" />
                </Button>
                {campaign.deleted_at ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Restore"
                    aria-label="Restore campaign"
                    onClick={() => onRestore(campaign)}
                  >
                    <RotateCcw className="text-muted-foreground" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    aria-label="Delete campaign"
                    onClick={() => onDelete(campaign)}
                  >
                    <Trash2 className="text-error" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-sm text-text-2">
              <Calendar className="size-4 shrink-0 text-muted-foreground" />
              <span className="tabular-nums">
                {formatDate(campaign.starts_at)} – {formatDate(campaign.ends_at)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border-subtle px-4 py-3">
            <span className="text-sm text-text-2">
              {campaign.status === 'active' ? 'On — switch off to pause' : 'Off — switch on to resume'}
            </span>
            {!campaign.deleted_at && (
              <Switch
                aria-label={campaign.status === 'active' ? 'Pause campaign' : 'Resume campaign'}
                checked={campaign.status === 'active'}
                onCheckedChange={() => onToggle(campaign)}
              />
            )}
          </div>
        </Card>
      ))}
    </div>
  )
}
